// Chọn hàng từ kho cho đơn — bảng riêng có ô tìm (tên / SKU, không cần gõ dấu), lọc theo tồn kho, sắp
// xếp. Bấm dòng = thêm 1 vào đơn; chỉnh số lượng ngay trong bảng. GET /products trả tối đa 200 dòng:
// kho lớn hơn thì ô tìm hỏi máy chủ.

import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Boxes, Loader2, Minus, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { SearchInput } from "@/components/ui/search-input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useDebounced } from "@/hooks/useDebounced"
import { useIsMobile } from "@/hooks/useIsMobile"
import { get } from "@/lib/api"
import { money, num } from "@/lib/format"
import { removeVietnameseAccents } from "@/lib/vietnamAddress"
import { cn } from "@/lib/utils"

export interface ProductOpt {
  id: string
  sku: string
  name: string
  price: number
  cost: number
  weightGram: number
  stock: number
  unit?: string
  lengthCm?: number | null
  widthCm?: number | null
  heightCm?: number | null
  imageUrl?: string | null
}

/** Số dòng tối đa GET /products trả về — danh sách đủ dài thế này có thể chưa phải cả kho. */
const LIST_CAP = 200
/** Cùng ngưỡng "Sắp hết" với trang Sản phẩm. */
const LOW_STOCK = 5

type StockFilter = "all" | "in" | "low" | "out"
type SortKey = "name" | "price-asc" | "price-desc" | "stock"

const FILTERS: [StockFilter, string][] = [
  ["all", "Tất cả"],
  ["in", "Còn hàng"],
  ["low", "Sắp hết"],
  ["out", "Hết hàng"],
]

const SORTS: [SortKey, string][] = [
  ["name", "Tên A–Z"],
  ["price-asc", "Giá thấp → cao"],
  ["price-desc", "Giá cao → thấp"],
  ["stock", "Còn nhiều nhất"],
]

const inFilter = (p: ProductOpt, f: StockFilter) =>
  f === "all" ? true : f === "in" ? p.stock > 0 : f === "low" ? p.stock > 0 && p.stock <= LOW_STOCK : p.stock <= 0

export function ProductPickerDialog({
  open,
  onOpenChange,
  products,
  inCart,
  cartQty,
  cartTotal,
  onSetQty,
  onCreateNew,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Danh sách sản phẩm đang bán đã tải ở form (undefined = đang tải) */
  products: ProductOpt[] | undefined
  /** productId → số lượng đang có trong đơn */
  inCart: Record<string, number>
  cartQty: number
  cartTotal: number
  /** Đặt số lượng của sản phẩm trong đơn; 0 = bỏ khỏi đơn */
  onSetQty: (p: ProductOpt, qty: number) => void
  /** Mở form thêm sản phẩm mới vào kho, điền sẵn tên đang tìm */
  /** Không truyền = không có nút thêm sản phẩm mới (nhân viên không có quyền quản lý sản phẩm) */
  onCreateNew?: (name: string) => void
}) {
  const isMobile = useIsMobile()
  const [q, setQ] = useState("")
  const [filter, setFilter] = useState<StockFilter>("all")
  const [sort, setSort] = useState<SortKey>("name")
  const debounced = useDebounced(q.trim(), 300)

  // Kho lớn hơn danh sách đã tải → tìm trên máy chủ (máy chủ so có dấu, lọc không dấu chạy thêm ở dưới)
  const partial = (products?.length ?? 0) >= LIST_CAP
  const { data: remote, isFetching: searching } = useQuery({
    queryKey: ["products-active", "search", debounced],
    queryFn: () => get<{ products: ProductOpt[] }>(`/products?active=true&q=${encodeURIComponent(debounced)}`),
    enabled: open && partial && debounced.length > 0,
    staleTime: 0,
  })
  const source = partial && debounced ? (remote?.products ?? []) : (products ?? [])

  const matched = useMemo(() => {
    const needle = removeVietnameseAccents(q)
    if (!needle) return source
    return source.filter((p) => removeVietnameseAccents(`${p.name} ${p.sku}`).includes(needle))
  }, [source, q])

  const counts = useMemo(() => {
    const c: Record<StockFilter, number> = { all: 0, in: 0, low: 0, out: 0 }
    for (const p of matched) for (const [f] of FILTERS) if (inFilter(p, f)) c[f]++
    return c
  }, [matched])

  const rows = useMemo(() => {
    const list = matched.filter((p) => inFilter(p, filter))
    const byName = (a: ProductOpt, b: ProductOpt) => a.name.localeCompare(b.name, "vi")
    return [...list].sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price || byName(a, b)
      if (sort === "price-desc") return b.price - a.price || byName(a, b)
      if (sort === "stock") return b.stock - a.stock || byName(a, b)
      return byName(a, b)
    })
  }, [matched, filter, sort])

  const lines = Object.values(inCart).filter((n) => n > 0).length
  const loading = !products || (partial && debounced.length > 0 && searching && !remote)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[88dvh] flex-col gap-0 overflow-hidden p-0 pb-0 sm:h-[min(44rem,85dvh)] sm:max-w-2xl sm:p-0">
        <div className="space-y-2.5 border-b border-rule px-4 pb-3 pt-3 sm:pt-5">
          <div className="pr-10">
            <DialogTitle className="text-base font-semibold">Chọn sản phẩm</DialogTitle>
            <DialogDescription className="text-xs">
              {products ? `${partial ? `${LIST_CAP}+` : num(products.length)} sản phẩm đang bán trong kho` : "Đang tải sản phẩm…"}
            </DialogDescription>
          </div>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              <SearchInput
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Tìm sản phẩm theo tên hoặc mã SKU"
                placeholder="Tên hoặc mã SKU"
                autoFocus={!isMobile}
                autoComplete="off"
                className="h-11 desktop:h-9"
              />
            </div>
            <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
              <SelectTrigger className="h-11 w-[8.5rem] shrink-0 desktop:h-9 sm:w-40" aria-label="Sắp xếp">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map(([v, label]) => (
                  <SelectItem key={v} value={v}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Lọc theo tồn kho">
            {FILTERS.map(([v, label]) => (
              <button
                key={v}
                type="button"
                aria-pressed={filter === v}
                onClick={() => setFilter(v)}
                className={cn(
                  "inline-flex h-8 cursor-pointer items-center gap-1 rounded-full px-3 text-xs",
                  filter === v ? "bg-accent font-semibold text-onAccent" : "bg-paperSoft text-inkSoft ring-1 ring-inset ring-rule hover:text-ink",
                )}
              >
                {label}
                <span className={cn("tabular-nums", filter === v ? "opacity-80" : "text-muted")}>{counts[v]}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {loading ? (
            <p className="flex items-center justify-center gap-2 py-12 text-sm text-muted">
              <Loader2 className="size-4 animate-spin" /> Đang tải sản phẩm…
            </p>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <p className="text-sm text-inkSoft">
                {source.length === 0 && !q
                  ? "Kho chưa có sản phẩm nào đang bán."
                  : q
                    ? `Không có sản phẩm nào khớp "${q.trim()}"${filter !== "all" ? " trong mục đang lọc" : ""}.`
                    : "Không có sản phẩm nào trong mục này."}
              </p>
              {onCreateNew && (
                <Button type="button" variant="outline" size="sm" className="text-accent" onClick={() => onCreateNew(q.trim())}>
                  <Plus /> Thêm sản phẩm
                </Button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-rule/60">
              {rows.map((p) => {
                const qty = inCart[p.id] ?? 0
                const out = p.stock <= 0
                const full = qty >= p.stock
                return (
                  <li key={p.id} className={cn("flex items-center gap-2 pr-3", qty > 0 && "bg-accent/5", out && "opacity-60")}>
                    <button
                      type="button"
                      disabled={out || full}
                      onClick={() => onSetQty(p, qty + 1)}
                      className="flex min-h-16 min-w-0 flex-1 cursor-pointer items-center gap-3 py-2 pl-4 text-left disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50"
                      aria-label={out ? `${p.name} — hết hàng` : `Thêm ${p.name} vào đơn`}
                    >
                      {p.imageUrl ? (
                        <img
                          src={p.imageUrl}
                          alt=""
                          loading="lazy"
                          className="size-12 shrink-0 rounded-lg object-cover ring-1 ring-rule"
                          onError={(e) => ((e.target as HTMLElement).style.display = "none")}
                        />
                      ) : (
                        <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-paperSoft text-muted">
                          <Boxes className="size-5 opacity-40" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 text-sm font-medium leading-snug text-ink">{p.name}</span>
                        <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs">
                          <span className="font-semibold tabular-nums text-accent">{money(p.price)}</span>
                          <span className={cn(out ? "font-medium text-danger" : p.stock <= LOW_STOCK ? "font-medium text-warn" : "text-muted")}>
                            {out ? "Hết hàng" : `Còn ${num(p.stock)}`}
                          </span>
                          <span className="truncate font-mono text-muted">{p.sku}</span>
                        </span>
                      </span>
                    </button>
                    {qty > 0 ? (
                      <div className="flex h-9 shrink-0 items-center rounded-md border border-accent/40 bg-raise">
                        <button
                          type="button"
                          onClick={() => onSetQty(p, qty - 1)}
                          aria-label={qty === 1 ? `Bỏ ${p.name} khỏi đơn` : `Giảm số lượng ${p.name}`}
                          className="flex h-full w-9 cursor-pointer items-center justify-center text-inkSoft hover:text-ink"
                        >
                          <Minus className="size-3.5" />
                        </button>
                        <span className="w-7 text-center font-mono text-sm font-semibold text-ink" aria-live="polite">
                          {qty}
                        </span>
                        <button
                          type="button"
                          onClick={() => onSetQty(p, qty + 1)}
                          disabled={full}
                          aria-label={`Tăng số lượng ${p.name}`}
                          className="flex h-full w-9 cursor-pointer items-center justify-center text-inkSoft hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Plus className="size-3.5" />
                        </button>
                      </div>
                    ) : (
                      !out && (
                        <button
                          type="button"
                          onClick={() => onSetQty(p, 1)}
                          aria-label={`Thêm ${p.name} vào đơn`}
                          className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-rule text-accent hover:border-accent/50 hover:bg-accent/5"
                        >
                          <Plus className="size-4" />
                        </button>
                      )
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-rule bg-raise px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
          <p className="min-w-0 flex-1 text-xs text-inkSoft">
            {lines > 0 ? (
              <>
                Đã chọn <b className="text-ink">{lines}</b> sản phẩm · {num(cartQty)} cái
                <span className="block font-mono text-sm font-semibold text-accent">{money(cartTotal)}</span>
              </>
            ) : (
              "Chưa chọn sản phẩm nào"
            )}
          </p>
          {onCreateNew && (
            <Button type="button" variant="outline" size="icon" className="text-accent" onClick={() => onCreateNew(q.trim())} aria-label="Thêm sản phẩm" title="Thêm sản phẩm">
              <Plus />
            </Button>
          )}
          <Button type="button" className="min-w-24" onClick={() => onOpenChange(false)}>
            Xong
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
